import { useTranslation } from "react-i18next";

import {
    MAX_READING_SPEED_WPM,
    MIN_READING_SPEED_WPM,
    paceLabelKey,
} from "../../domain/readingSpeed";
import Label from "../../../../shared/ui/form/Label";
import { formatDuration } from "../../domain/types";

type Props = {
    value: number;
    onChange: (next: number) => void;
    /** How long the script runs at this pace, so the slider has consequences. */
    estimatedSeconds: number;
    /** What the author says it takes, in minutes; empty until they say. */
    declaredMinutes?: number | null;
    onDeclaredMinutesChange?: (next: number | null) => void;
};

/**
 * How fast the prompter will move.
 *
 * A slider rather than a number field, because nobody knows what 90 words per
 * minute feels like — but everybody knows whether a meditation should run six
 * minutes or twelve. The named pace and the resulting length are both shown,
 * so the control is read through its effect rather than through its unit.
 */
export default function ReadingSpeedField({
    value,
    onChange,
    estimatedSeconds,
    declaredMinutes,
    onDeclaredMinutesChange,
}: Props) {
    const { t } = useTranslation();

    return (
        <div>
            <label
                htmlFor="meditation-pace"
                className="mb-1.5 block text-sm font-medium text-content-secondary"
            >
                {t("meditation.fields.readingSpeed")}
            </label>
            <input
                id="meditation-pace"
                type="range"
                min={MIN_READING_SPEED_WPM}
                max={MAX_READING_SPEED_WPM}
                step={5}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full accent-[var(--color-accent)]"
                aria-describedby="meditation-pace-hint"
            />
            <p
                id="meditation-pace-hint"
                className="mt-1 flex flex-wrap justify-between gap-2 text-xs text-content-tertiary"
            >
                <span>{t(paceLabelKey(value))}</span>
                <span className="tabular-nums">
                    {t("meditation.fields.readingSpeedValue", { wpm: value })}
                </span>
                {estimatedSeconds > 0 && (
                    <span className="tabular-nums">
                        {t("meditation.fields.readingSpeedDuration", {
                            duration: formatDuration(estimatedSeconds),
                        })}
                    </span>
                )}
            </p>

            {onDeclaredMinutesChange && (
                // Beside the arithmetic rather than instead of it: the
                // prompter's figure is the reading, and a therapist who has
                // guided this twenty times knows what it takes with somebody
                // in the room.
                <div className="mt-4">
                    <Label htmlFor="meditation-declared">
                        {t("meditation.fields.declaredDuration")}
                    </Label>
                    <div className="flex items-center gap-2">
                        <input
                            id="meditation-declared"
                            type="number"
                            min={1}
                            max={180}
                            inputMode="numeric"
                            className="field-base field-default h-11 w-24 px-3"
                            placeholder={String(Math.round(estimatedSeconds / 60) || 10)}
                            value={declaredMinutes ?? ""}
                            onChange={(e) => {
                                const minutes = Number(e.target.value);
                                onDeclaredMinutesChange(
                                    e.target.value === "" || !Number.isFinite(minutes)
                                        ? null
                                        : Math.min(180, Math.max(1, Math.round(minutes)))
                                );
                            }}
                        />
                        <span className="text-sm text-content-secondary">
                            {t("meditation.fields.minutes")}
                        </span>
                    </div>
                    <p className="mt-1 text-xs text-content-tertiary">
                        {t("meditation.fields.declaredDurationHint")}
                    </p>
                </div>
            )}
        </div>
    );
}
